// @flow
import * as React from 'react';
import { Box, Button, Text } from 'grommet';
import {
    get, intersectionBy, isEmpty,
} from 'lodash';
import { GeoJSON } from 'leaflet';
import { withLeaflet } from 'react-leaflet';
import type { LeafletContext } from 'react-leaflet/src';
import { Close } from 'grommet-icons';
import { FormattedMessage, injectIntl, IntlShape } from 'react-intl';
import lineMessages from 'esm-web-pro/messages/lineMessages';
import HorizontalLine from 'esm-web-pro/components/layout/HorizontalLine';
import UseHighlightLine from 'esm-web-pro/hooks/useHighlightLine';
import { numberedPointIcon, pointIcon } from 'esm-web-pro/containers/pages/network/Util';
import BaseEditor from 'esm-web-pro/containers/map/editor/BaseEditor';
import { pointsHighlightCallback } from 'esm-web-pro/containers/pages/network/highlight';
import { APIContext, withConfirmation } from 'speed-js-react';
import type { Confirmation } from 'speed-js-react';


type Props = {
    editor: BaseEditor,

    intl: IntlShape,
    leaflet: LeafletContext,
    confirm: (confirmation: Confirmation) => void,
};

let pointsWithHandlers = [];
let disabledPoints = [];

// Function that returns two digit representation of a number
const twoD = (number) => String(number).padStart(2, '0');

const MeasureRoutes = (props: Props) => {
    const {
        editor, leaflet: { map }, intl: { formatMessage }, confirm,
    } = props;
    const [lines, setLines] = React.useState([]);
    const [lengths, setLengths] = React.useState({});
    const [selectedPoints, setSelectedPoints] = React.useState([]);
    const [total, setTotal] = React.useState(0);

    const { apiClient } = React.useContext(APIContext);

    const handlePointClick = async (e: { target: Object }) => {
        const newPointLayer = e.target;

        const icon = numberedPointIcon(newPointLayer, selectedPoints.length + 1);
        newPointLayer.setIcon(icon);
        if (isEmpty(selectedPoints)) {
            setSelectedPoints([newPointLayer]);
            return;
        }

        const lastPointLayer = selectedPoints[selectedPoints.length - 1];
        const lastPointLines = map.coordsToLayers[lastPointLayer.toGeoJSON().geometry.coordinates.join('-')].lines;
        const newPointLines = map.coordsToLayers[newPointLayer.toGeoJSON().geometry.coordinates.join('-')].lines;
        const [line] = intersectionBy(lastPointLines, newPointLines, ((lineLayer) => lineLayer.getLatLngs()));

        let length;

        try {
            length = await apiClient.resources.gisLine.length(line.feature.id);
        } catch (error) {
            confirm({
                message: <FormattedMessage
                    defaultMessage="The line length could not be retrieved successfully."
                    id="gis-line-length-fetch-failure-confirm"
                />,
                actions: {
                    confirm: {
                        label: <FormattedMessage defaultMessage="Ok" id="ok" />,
                        callback: () => {
                        },
                        type: 'negative',
                    },
                },
            });
        }

        if (length) {
            setTotal(+(total + +length).toFixed(2));
            setLines([...lines, line]);
            setLengths({ ...lengths, [line.feature.id]: length.toFixed(2) });
            setSelectedPoints([...selectedPoints, newPointLayer]);
        }
    };

    const pointClickCallback = {
        run: (layer) => {
            if (get(layer, 'feature.geometry.type') !== 'Point' || selectedPoints.includes(layer)
            || !['networks-point-icon', 'networks-point-div-icon']
                .includes(layer.options?.icon?.options?.className)) return;

            if (isEmpty(selectedPoints)) {
                layer.on('click', handlePointClick);
                pointsWithHandlers.push(layer);
                return;
            }

            const lastPointLayer = selectedPoints[selectedPoints.length - 1];
            const lastPointLines = map.coordsToLayers[lastPointLayer.toGeoJSON().geometry.coordinates.join('-')]?.lines;
            const layerLines = map.coordsToLayers[layer.toGeoJSON().geometry.coordinates.join('-')]?.lines;
            if (lastPointLines && layerLines
                && !isEmpty(intersectionBy(lastPointLines, layerLines, ((lineLayer) => lineLayer.getLatLngs())))) {
                layer.on('click', handlePointClick);
                pointsWithHandlers.push(layer);
            } else {
                const { point } = layer.feature.properties;
                const disabledIcon = point ? pointIcon(point.child.type, true, point.color,
                    point.child.underground_connection, point.content_count) : pointIcon(point, true);
                layer.setIcon(disabledIcon);
                disabledPoints.push(layer);
            }
        },
        cleanUp: () => {
            pointsWithHandlers.forEach((point) => point.off('click', handlePointClick));
            disabledPoints.forEach((layer) => {
                const { point } = layer.feature.properties;
                const icon = point ? pointIcon(point.child.type, false, point.color,
                    point.child.underground_connection, point.content_count) : pointIcon(point, true);
                layer.setIcon(icon);
            });
            pointsWithHandlers = [];
            disabledPoints = [];
        },
    };

    React.useEffect(() => {
        const selectedPointHighlightCallback = pointsHighlightCallback(selectedPoints, handlePointClick);
        const selectedLinesHighlightCallbacks = lines.map(
            (line: Object) => UseHighlightLine(
                GeoJSON.latLngsToCoords(line.getLatLngs()).map((c) => [c[1], c[0]]), [{ weight: 10 }, { weight: 3 }],
            ),
        );
        // Update points click handlers when map viewport is changed
        editor.registerMultipleLoadingCallbacks([
            pointClickCallback,
            selectedPointHighlightCallback,
            ...selectedLinesHighlightCallbacks,
        ]);
        // Update points click handlers when a point is clicked
        map.eachLayer((layer) => editor.applyLoadingCallbacks(layer));
        return () => {
            editor.unregisterMultipleLoadingCallbacks([
                pointClickCallback,
                selectedPointHighlightCallback,
                ...selectedLinesHighlightCallbacks,
            ]);
        };
    }, [selectedPoints]);

    const onLastRemove = () => {
        setSelectedPoints(selectedPoints.slice(0, selectedPoints.length - 1));
        if (lines.length > 0) {
            setTotal(+(total - +lengths[lines[lines.length - 1].feature.id]).toFixed(2));
            setLines(lines.slice(0, lines.length - 1));
        }
    };

    const renderLines = () => (
        <Box flex={{ shrink: 0 }}>
            {selectedPoints.length === 1 ? (
                <Box align="center" direction="row" justify="center" key={selectedPoints[0]._leaflet_id}>
                    <Box margin={{ vertical: 'small' }} width="100%">
                        <Text>{twoD(1)}</Text>
                    </Box>
                    <Button alignSelf="center" icon={<Close />} onClick={() => onLastRemove(selectedPoints[0])} />
                </Box>
            ) : selectedPoints.map((point, index) => (lines.length > index && (
                <Box align="center" direction="row" justify="center" key={point._leaflet_id}>
                    <Box margin={{ vertical: 'small' }} width="100%">
                        {/* eslint-disable-next-line max-len */}
                        <Text>
                            {`${twoD(index + 1)} -
                            ${formatMessage(lineMessages.lineLength, { value: lengths[lines[index].feature.id] })}
                            - ${twoD(index + 2)}`}
                        </Text>
                    </Box>
                    {index === selectedPoints.length - 2 && (
                        <Button
                            alignSelf="center"
                            icon={<Close />}
                            onClick={() => onLastRemove(selectedPoints[index + 1])}
                        />
                    )}
                </Box>
            )))}
        </Box>
    );

    return (
        <Box>
            <Box
                direction="row"
                gap="medium"
                pad={{ bottom: 'small', horizontal: 'medium' }}
                align="center"
                height="100%"
                width="100%"
                overflow="auto"
                style={{ position: 'unset' }}
            >
                <Box alignSelf="stretch" flex>
                    {renderLines()}
                    {total > 0 && (<HorizontalLine color="grey" />)}
                    <Box pad={{ top: 'small' }} justify="start" direction="row">
                        <Box margin={{ right: 'small' }}>
                            <Text>
                                <FormattedMessage id="total" defaultMessage="Total" />
                            </Text>
                        </Box>
                        <Text>
                            {formatMessage(lineMessages.lineLength, { value: total })}
                        </Text>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default withConfirmation(injectIntl(withLeaflet(MeasureRoutes)));
